import { AiOutlineLoading } from "react-icons/ai";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Checkbox,
  Label,
} from "flowbite-react";
import { useForm } from "react-hook-form";
import { ADMIN } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useState, useMemo } from "react";
import { HiInformationCircle } from "react-icons/hi";
import { useQuery } from "react-query";
import Input from "../inputs/Input";
import DrawerLoading from "../DrawerLoading";
import CurrencyInput from "../inputs/CurrencyInput";

const TableQueriesForm = ({
  categories,
  fetchTables,
  onClose,
  selectedTable,
  type,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const { isLoading: isLoadingCompanyQueries, data: tableQueries } = useQuery(
    ["tableQueries", selectedTable],
    async () => {
      if (!selectedTable) return null;
      const { data: items } = await axios.get(
        `${ADMIN.GET_TABLE_QUERIES}/${selectedTable?.id}`
      );
      return items.data;
    }
  );

  const { register, handleSubmit, reset, setValue, watch, control } = useForm();

  // ===== Prefill =====
  useEffect(() => {
    reset();
    setError(null);

    if (selectedTable) {
      setValue("name", selectedTable?.name);
      setValue("vcm", selectedTable?.vcm);
      setValue("hasSurvey", selectedTable?.hasSurvey);
      setValue("hasCV", selectedTable?.hasCV);
      setValue("priceSurvey", selectedTable?.priceSurvey);
      setValue("priceCV", selectedTable?.priceCV);
    }

    categories?.forEach((category, catIdx) => {
      category?.Queries?.forEach((query, index) => {
        setValue(`categories[${catIdx}].queries[${index}].QueryId`, query.id);

        const tableQuery =
          tableQueries?.find((cq) => cq.QueryId === query.id) || null;

        if (tableQuery) {
          setValue(`categories[${catIdx}].queries[${index}].isActive`, true);
          setValue(
            `categories[${catIdx}].queries[${index}].price`,
            tableQuery.price
          );
        } else {
          setValue(
            `categories[${catIdx}].queries[${index}].price`,
            query.price
          );
        }
      });
    });
  }, [tableQueries, selectedTable, type, categories, reset, setValue]);

  // ===== Submit =====
  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      let flat = [];
      (data.categories || []).forEach((cat) => {
        (cat.queries || []).forEach((q) => flat.push(q));
      });
      data.queries = flat.filter((q) => q.isActive);

      if (data.vcm === "") delete data.vcm;

      if (selectedTable) {
        await axios.put(`${ADMIN.GET_TABLE_QUERIES}/${selectedTable.id}`, data);
      } else {
        await axios.post(`${ADMIN.GET_TABLE}`, data);
      }

      onClose(false);
      fetchTables?.();
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===== Helpers UI =====
  const CountBadge = ({ count, label }) => (
    <div className="inline-flex items-center gap-2 text-xs text-gray-600">
      <Badge color="info">{count}</Badge>
      <span>{label}</span>
    </div>
  );

  const SectionHeader = ({ title, right }) => (
    <div className="flex items-center justify-between mb-2">
      <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide underline">
        {title}
      </div>
      {right}
    </div>
  );

  const Row = ({ children, active }) => (
    <div
      className={[
        "border-b p-2 transition-all bg-white",
        active ? " " : "border-gray-200 hover:border-gray-300",
      ].join(" ")}
    >
      {children}
    </div>
  );

  const Hint = ({ children }) => (
    <div className="mt-1 text-[11px] text-gray-500">{children}</div>
  );

  // ===== Render =====
  return (
    <>
      <form className="mb-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {/* Header card */}
        <div className="rounded-2xl border shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div className="flex-1">
              <Input
                className="mb-4"
                label="Nome da tabela"
                register={register}
                name="name"
                required={true}
              />
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <CurrencyInput
                    className="mb-1"
                    label="VCM"
                    register={register}
                    name="vcm"
                    control={control}
                  />
                  <Hint>VCM: Valor de custo mínimo mensal</Hint>
                </div>
              </div>
            </div>

            {type !== "CREATE" && (
              <div className="rounded-xl border bg-white p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox id="accept" {...register("updateAll")} />
                  <span className="text-sm">
                    Atualizar preços para todos os clientes desta tabela
                  </span>
                </label>
                <Hint>Esta ação aplica imediatamente as alterações.</Hint>
              </div>
            )}
          </div>
        </div>

        {categories?.length > 0 ? (
          <>
            <Accordion className="w-full">
              {categories?.map((category, idxCat) => {
                const mainCount =
                  category?.Queries?.filter((q) => !q.isAdditional).length || 0;
                const addCount =
                  category?.Queries?.filter((q) => q.isAdditional).length || 0;

                return (
                  <Accordion.Panel key={idxCat}>
                    <Accordion.Title>
                      <div className="flex items-center justify-between w-full gap-3">
                        <span>{category.name}</span>
                        <div className="flex items-center gap-3">
                          <CountBadge count={mainCount} label="principais" />
                          <CountBadge count={addCount} label="adicionais" />
                        </div>
                      </div>
                    </Accordion.Title>

                    <Accordion.Content>
                      <div className="flex flex-col gap-6">
                        {/* PRINCIPAIS */}
                        <div className="">
                          <SectionHeader title="Consultas principais" />
                          {category?.Queries?.some((q) => !q.isAdditional) ? (
                            <div className="flex flex-col gap-3">
                              {category.Queries.map((query, index) => {
                                if (query.isAdditional) return null;

                                const isActive = !!watch(
                                  `categories[${idxCat}].queries[${index}].isActive`
                                );

                                return (
                                  <Row
                                    key={`main-${idxCat}-${index}`}
                                    active={isActive}
                                  >
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                      <label className="flex items-center cursor-pointer gap-3">
                                        <Checkbox
                                          id={`categories-${idxCat}-queries-${index}-isActive`}
                                          {...register(
                                            `categories[${idxCat}].queries[${index}].isActive`
                                          )}
                                          checked={isActive}
                                        />
                                        <span className="text-sm font-medium text-gray-900">
                                          {query.name}
                                        </span>
                                      </label>

                                      <div className="w-full sm:w-48">
                                        <CurrencyInput
                                          control={control}
                                          register={register}
                                          name={`categories[${idxCat}].queries[${index}].price`}
                                          label="Preço"
                                          className="w-full"
                                          disabled={!isActive}
                                        />
                                        {!isActive && (
                                          <Hint>Ative para editar o preço</Hint>
                                        )}
                                      </div>
                                    </div>
                                  </Row>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 px-1 py-2">
                              Esta categoria não possui consultas principais.
                            </div>
                          )}
                        </div>

                        {/* ADICIONAIS */}
                        <div className="">
                          <SectionHeader title="Consultas adicionais" />
                          {category?.Queries?.some((q) => !!q.isAdditional) ? (
                            <div className="flex flex-col gap-3">
                              {category.Queries.map((query, index) => {
                                if (!query.isAdditional) return null;

                                const isActive = !!watch(
                                  `categories[${idxCat}].queries[${index}].isActive`
                                );

                                return (
                                  <Row
                                    key={`add-${idxCat}-${index}`}
                                    active={isActive}
                                  >
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                      <label className="flex items-center cursor-pointer gap-3">
                                        <Checkbox
                                          id={`categories-${idxCat}-queries-${index}-isActive`}
                                          {...register(
                                            `categories[${idxCat}].queries[${index}].isActive`
                                          )}
                                          checked={isActive}
                                        />
                                        <span className="text-sm font-medium text-gray-900">
                                          {query.name}
                                        </span>
                                      </label>

                                      <div className="w-full sm:w-48">
                                        <CurrencyInput
                                          control={control}
                                          register={register}
                                          name={`categories[${idxCat}].queries[${index}].price`}
                                          label="Preço"
                                          className="w-full"
                                          disabled={!isActive}
                                        />
                                        {!isActive && (
                                          <Hint>Ative para editar o preço</Hint>
                                        )}
                                      </div>
                                    </div>
                                  </Row>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 px-1 py-2">
                              Esta categoria não possui consultas adicionais.
                            </div>
                          )}
                        </div>
                      </div>
                    </Accordion.Content>
                  </Accordion.Panel>
                );
              })}
            </Accordion>
            <div className="flex justify-end">
              <Button
                type="submit"
                className="w-full md:w-auto md:min-w-[220px] primary mt-4"
                isProcessing={isSubmitting}
                processingSpinner={
                  <AiOutlineLoading className="h-6 w-6 animate-spin" />
                }
                disabled={isSubmitting}
              >
                {type === "CREATE" ? "Criar tabela" : "Atualizar tabela"}
              </Button>
            </div>

            {error && (
              <Alert
                color="failure"
                icon={HiInformationCircle}
                className="mt-3"
              >
                Erro ao {type === "CREATE" ? "criar" : "atualizar"} a tabela.
              </Alert>
            )}
          </>
        ) : (
          <div className="text-sm md:text-md text-center rounded-xl border bg-white p-6">
            Não há nenhuma categoria cadastrada. <br />
            Comece cadastrando as categorias primeiro.
          </div>
        )}
      </form>

      {isLoadingCompanyQueries && <DrawerLoading />}
    </>
  );
};

export default TableQueriesForm;
